import {
  type SheepPurposeT,
  type CattleBreedTypeT,
  type SheepBreedTypeT,
  type CattleSpeciesT,
} from '@ruminati/types_v2/livestock_system'

export interface BreedCharacteristics {
  breedMultiplier: number
  typicalJoiningAge: number // months
}

export const CATTLE_BREED_CHARACTERISTICS: Record<CattleSpeciesT, BreedCharacteristics> = {
  // (Angus, Hereford, etc.)
  taurus: {
    breedMultiplier: 1.0,

    typicalJoiningAge: 15,
  },
  // (Brahman, etc.)
  indicus: {
    breedMultiplier: 0.85,
    typicalJoiningAge: 24,
  },
  // Hybrid vigor, between the two
  crossbred: {
    breedMultiplier: 0.93,
    typicalJoiningAge: 18,
  },
}

export const CATTLE_BREEDS: {
  breed: CattleBreedTypeT
  speciesType: CattleSpeciesT
  matureWeights: { small: number; medium: number; large: number }
}[] = [
  { breed: 'Angus', speciesType: 'taurus', matureWeights: { small: 450, medium: 550, large: 650 } },
  {
    breed: 'Hereford',
    speciesType: 'taurus',
    matureWeights: { small: 500, medium: 600, large: 700 },
  },
  {
    breed: 'Shorthorn',
    speciesType: 'taurus',
    matureWeights: { small: 500, medium: 600, large: 700 },
  },
  {
    breed: 'Murray Grey',
    speciesType: 'taurus',
    matureWeights: { small: 450, medium: 550, large: 650 },
  },
  {
    breed: 'Limousin',
    speciesType: 'taurus',
    matureWeights: { small: 550, medium: 650, large: 750 },
  },
  {
    breed: 'Charolais',
    speciesType: 'taurus',
    matureWeights: { small: 600, medium: 700, large: 850 },
  },
  {
    breed: 'Simmental',
    speciesType: 'taurus',
    matureWeights: { small: 600, medium: 700, large: 850 },
  },
  { breed: 'Devon', speciesType: 'taurus', matureWeights: { small: 400, medium: 500, large: 600 } },
  {
    breed: 'Galloway',
    speciesType: 'taurus',
    matureWeights: { small: 400, medium: 500, large: 600 },
  },
  {
    breed: 'Red Poll',
    speciesType: 'taurus',
    matureWeights: { small: 450, medium: 550, large: 650 },
  },
  {
    breed: 'Brahman',
    speciesType: 'indicus',
    matureWeights: { small: 450, medium: 550, large: 650 },
  },
  {
    breed: 'Droughtmaster',
    speciesType: 'crossbred',
    matureWeights: { small: 450, medium: 550, large: 650 },
  },
  {
    breed: 'Santa Gertrudis',
    speciesType: 'crossbred',
    matureWeights: { small: 550, medium: 650, large: 750 },
  },
  {
    breed: 'Braford',
    speciesType: 'crossbred',
    matureWeights: { small: 500, medium: 600, large: 700 },
  },
  {
    breed: 'Brangus',
    speciesType: 'crossbred',
    matureWeights: { small: 450, medium: 550, large: 650 },
  },
  {
    breed: 'Charbray',
    speciesType: 'crossbred',
    matureWeights: { small: 600, medium: 700, large: 850 },
  },
  {
    breed: 'Belmont Red',
    speciesType: 'crossbred',
    matureWeights: { small: 450, medium: 550, large: 650 },
  },
  {
    breed: 'Senepol',
    speciesType: 'taurus',
    matureWeights: { small: 450, medium: 550, large: 650 },
  },
  {
    breed: 'Speckle Park',
    speciesType: 'taurus',
    matureWeights: { small: 500, medium: 600, large: 700 },
  },
  { breed: 'Wagyu', speciesType: 'taurus', matureWeights: { small: 400, medium: 500, large: 600 } },
  {
    breed: 'Holstein Friesian',
    speciesType: 'taurus',
    matureWeights: { small: 550, medium: 650, large: 750 },
  },
]

export const SHEEP_BREED_CHARACTERISTICS: Record<SheepPurposeT, BreedCharacteristics> = {
  // Merinos - slower growing, wool focused
  merino: {
    breedMultiplier: 0.85,
    typicalJoiningAge: 18, // Maiden mating
  },

  // Terminal sires - fast growing meat breeds (Dorper, White Suffolk, etc.)
  meat: {
    breedMultiplier: 1.15,
    typicalJoiningAge: 8, // Can join as hoggets
  },

  // Dual purpose - moderate (Border Leicester, Corriedale, etc.)
  'dual-purpose': {
    breedMultiplier: 1.0,
    typicalJoiningAge: 12,
  },
}

export const SHEEP_BREEDS: {
  breed: SheepBreedTypeT
  primaryUse: SheepPurposeT
  matureWeights: { small: number; medium: number; large: number }
}[] = [
  { breed: 'Merino', primaryUse: 'merino', matureWeights: { small: 45, medium: 55, large: 65 } },
  {
    breed: 'Border Leicester',
    primaryUse: 'dual-purpose',
    matureWeights: { small: 65, medium: 75, large: 85 },
  },
  {
    breed: 'Corriedale',
    primaryUse: 'dual-purpose',
    matureWeights: { small: 55, medium: 65, large: 75 },
  },
  { breed: 'Dorset', primaryUse: 'meat', matureWeights: { small: 60, medium: 70, large: 80 } },
  { breed: 'Suffolk', primaryUse: 'meat', matureWeights: { small: 70, medium: 80, large: 90 } },
  {
    breed: 'White Suffolk',
    primaryUse: 'meat',
    matureWeights: { small: 70, medium: 80, large: 90 },
  },
  { breed: 'Poll Dorset', primaryUse: 'meat', matureWeights: { small: 60, medium: 70, large: 80 } },
  { breed: 'Dorper', primaryUse: 'meat', matureWeights: { small: 55, medium: 65, large: 75 } },
  { breed: 'Texel', primaryUse: 'meat', matureWeights: { small: 65, medium: 75, large: 85 } },
  { breed: 'Southdown', primaryUse: 'meat', matureWeights: { small: 50, medium: 60, large: 70 } },
  {
    breed: 'Romney',
    primaryUse: 'dual-purpose',
    matureWeights: { small: 55, medium: 65, large: 75 },
  },
  {
    breed: 'Coopworth',
    primaryUse: 'dual-purpose',
    matureWeights: { small: 60, medium: 70, large: 80 },
  },
  {
    breed: 'Perendale',
    primaryUse: 'dual-purpose',
    matureWeights: { small: 55, medium: 65, large: 75 },
  },
  {
    breed: 'Poll Merino',
    primaryUse: 'merino',
    matureWeights: { small: 45, medium: 55, large: 65 },
  },
  {
    breed: 'Comeback',
    primaryUse: 'dual-purpose',
    matureWeights: { small: 50, medium: 60, large: 70 },
  },
  {
    breed: 'First Cross',
    primaryUse: 'dual-purpose',
    matureWeights: { small: 55, medium: 65, large: 75 },
  },
  {
    breed: 'Dohne Merino',
    primaryUse: 'dual-purpose',
    matureWeights: { small: 50, medium: 60, large: 70 },
  },
  {
    breed: 'Australian White',
    primaryUse: 'meat',
    matureWeights: { small: 65, medium: 75, large: 85 },
  },
  { breed: 'Damara', primaryUse: 'meat', matureWeights: { small: 50, medium: 60, large: 70 } },
  { breed: 'Wiltipoll', primaryUse: 'meat', matureWeights: { small: 60, medium: 70, large: 80 } },
]
